import { useEffect, useState } from "react";
import type { Checklist, ChecklistItem, ItemComment } from "@closing/shared";
import { useAuth } from "../lib/auth";
import {
  addComment,
  deleteComment,
  setItemGate,
  setItemYesNo,
  watchItemComments,
} from "../lib/db";
import { extractMentions, segmentText } from "../lib/mentions";
import MentionInput from "./MentionInput";

/**
 * Right-side panel for one item: per-deal settings (gate, Yes/No answer
 * chip) and the comment thread. Comments live in the checklist's comments
 * subcollection; the item's commentCount is denormalized on write.
 */
export default function CommentsDrawer({
  dealId,
  checklist,
  item,
  onClose,
}: {
  dealId: string;
  checklist: Checklist;
  item: ChecklistItem;
  onClose: () => void;
}) {
  const { user } = useAuth();
  const [comments, setComments] = useState<(ItemComment & { id: string })[]>([]);
  const [draft, setDraft] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(
    () => watchItemComments(dealId, item.id, setComments),
    [dealId, item.id],
  );

  async function submit() {
    const text = draft.trim();
    if (!text || busy) return;
    setBusy(true);
    try {
      const mentions = [...new Set(extractMentions(text).map((m) => m.email))];
      await addComment(dealId, item.id, text, mentions);
      setDraft("");
    } catch (err) {
      window.alert(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      {/* Click-away backdrop */}
      <div className="fixed inset-0 z-30 bg-ink/20" onClick={onClose} />
      <aside className="fixed inset-y-0 right-0 z-40 flex w-full max-w-md flex-col border-l border-line bg-white shadow-2xl">
        <header className="flex items-start justify-between gap-3 border-b border-line px-5 py-4">
          <div className="min-w-0">
            <p className="eyebrow text-signature">Item</p>
            <h2 className="mt-1 truncate text-sm font-semibold text-ink" title={item.name}>
              {item.name}
            </h2>
          </div>
          <button
            onClick={onClose}
            className="font-mono text-sm text-muted hover:text-ink"
            aria-label="Close"
          >
            ✕
          </button>
        </header>

        {/* Per-deal item settings */}
        <div className="space-y-3 border-b border-line px-5 py-4">
          <div className="flex items-center gap-3">
            <span className="w-20 flex-none font-mono text-[10px] uppercase tracking-wider text-muted">
              Gated by
            </span>
            <GateSelect dealId={dealId} checklist={checklist} item={item} />
          </div>
          <div className="flex items-center gap-3">
            <span className="w-20 flex-none font-mono text-[10px] uppercase tracking-wider text-muted">
              Yes/No
            </span>
            <label className="flex cursor-pointer items-center gap-2 text-[12px] text-ink-soft">
              <input
                type="checkbox"
                checked={!!item.hasYesNo}
                onChange={(e) =>
                  void setItemYesNo(dealId, item.id, e.target.checked).catch((err) =>
                    window.alert(err instanceof Error ? err.message : String(err)),
                  )
                }
                className="accent-[#bb2027]"
              />
              show a Yes/No answer on this item
            </label>
          </div>
        </div>

        <div className="flex-1 space-y-4 overflow-y-auto px-5 py-4">
          {comments.length === 0 && (
            <p className="py-8 text-center font-mono text-[11px] text-faint">
              No comments yet.
            </p>
          )}
          {comments.map((c) => (
            <div key={c.id} className="group">
              <div className="flex items-baseline justify-between gap-2">
                <span className="text-[12px] font-semibold text-ink">{c.authorName}</span>
                <span className="flex items-center gap-2">
                  <Timestamp ts={c.createdAt} />
                  {c.authorUid === user?.uid && (
                    <button
                      onClick={() => void deleteComment(dealId, item.id, c.id)}
                      className="font-mono text-[10px] text-faint opacity-0 transition-opacity hover:text-flag group-hover:opacity-100"
                      title="Delete comment"
                    >
                      delete
                    </button>
                  )}
                </span>
              </div>
              <p className="mt-1 whitespace-pre-wrap text-[13px] leading-relaxed text-ink-soft">
                {segmentText(c.text).map((seg, i) =>
                  seg.type === "mention" ? (
                    <span key={i} className="font-semibold text-signature">
                      @{seg.displayName}
                    </span>
                  ) : (
                    <span key={i}>{seg.value}</span>
                  ),
                )}
              </p>
            </div>
          ))}
        </div>

        <footer className="border-t border-line px-5 py-4">
          <MentionInput
            value={draft}
            onChange={setDraft}
            onSubmit={() => void submit()}
            placeholder="Comment — @ to mention, Enter to send"
            disabled={busy}
          />
        </footer>
      </aside>
    </>
  );
}

/**
 * Per-deal gate picker. Candidates exclude the item itself and anything that
 * transitively depends on it (picking those would close a locked-forever
 * cycle); the write re-validates against current data in a transaction.
 */
function GateSelect({
  dealId,
  checklist,
  item,
}: {
  dealId: string;
  checklist: Checklist;
  item: ChecklistItem;
}) {
  const items = checklist.items;
  const reaches = (fromId: string, targetId: string, seen = new Set<string>()): boolean => {
    if (fromId === targetId) return true;
    if (seen.has(fromId)) return false;
    seen.add(fromId);
    return (items[fromId]?.dependsOn ?? []).some((d) => reaches(d, targetId, seen));
  };
  const options = Object.values(items)
    .filter((i) => i.id !== item.id && !reaches(i.id, item.id))
    .sort((a, b) => a.name.localeCompare(b.name));

  return (
    <select
      value={item.dependsOn?.[0] ?? ""}
      onChange={(e) =>
        void setItemGate(dealId, item.id, e.target.value || null).catch((err) =>
          window.alert(err instanceof Error ? err.message : String(err)),
        )
      }
      className="min-w-0 flex-1 rounded border border-line bg-white px-2 py-1 font-mono text-[11px] text-ink-soft"
    >
      <option value="">— not gated —</option>
      {options.map((i) => (
        <option key={i.id} value={i.id}>
          {i.name.length > 48 ? i.name.slice(0, 47) + "…" : i.name}
        </option>
      ))}
    </select>
  );
}

function Timestamp({ ts }: { ts: unknown }) {
  const date =
    ts && typeof (ts as { toDate?: () => Date }).toDate === "function"
      ? (ts as { toDate: () => Date }).toDate()
      : null;
  return (
    <span className="font-mono text-[10px] text-faint">
      {date
        ? date.toLocaleString(undefined, {
            month: "short",
            day: "numeric",
            hour: "numeric",
            minute: "2-digit",
          })
        : "…"}
    </span>
  );
}
